import * as React from 'react'
import moment from 'moment'
import { useDate } from '@/modules/drivers/stores/dateStore'
import useBusinessTripService from '../services/BusinessTripService'
import { BusinessTripModel } from '@/app/models/businessTripModel'
import Single from '../tabs/businessTrips/views/Single'
import { DatePickerRangeProps } from '../types'
import { SERVICE_DATE_FORMAT } from '@/app/constants/date'
import { addMonths, format, subMonths } from 'date-fns'
import { TableProvider } from '../context/TableContext'
import { DateRangeFilter } from '@/app/constants/delegation/dateRangeFilter'

const BusinessTripsContent = ({
  match,
  history,
  companyId,
  driverId,
}: {
  match: any
  history: any
  companyId: string
  driverId: string
}) => {
  const { setSingle, formatApiDate } = useDate()
  const { getBusinessTrips } = useBusinessTripService()
  const [trips, setTrips] = React.useState<BusinessTripModel[]>([])
  const [dateRangeFilter, setDateRangeFilter] = React.useState<DateRangeFilter>(
    DateRangeFilter.Started,
  )
  const [polishOnlyFilter, setPolishOnlyFilter] = React.useState<boolean>(false)
  const [refreshKey, setRefreshKey] = React.useState(0)

  const { params } = match
  const { from, to } = params

  React.useEffect(() => {
    if (to && to.match(/^\d{4}-\d{2}-\d{2}$/)) {
      setSingle(to)
    }
  }, [to, setSingle])

  const handleRefresh = React.useCallback(() => {
    setRefreshKey((prev) => prev + 1)
  }, [])

  const convertToDatePickerProps = (
    dateString: string | undefined,
  ): DatePickerRangeProps['startDate'] | null => {
    if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return null
    }
    const parsed = moment(dateString, SERVICE_DATE_FORMAT, true)
    if (!parsed.isValid()) {
      return null
    }
    const date = parsed.toDate()
    if (isNaN(date.getTime())) {
      return null
    }
    return {
      fullDate: format(date, 'DD-MM-YY'),
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    }
  }

  const [selectedDate, setSelectedDate] = React.useState<DatePickerRangeProps>(
    () => {
      const start = convertToDatePickerProps(from)
      const end = convertToDatePickerProps(to)
      return {
        startDate: start,
        endDate: end,
      }
    },
  )

  React.useEffect(() => {
    const start = convertToDatePickerProps(from)
    const end = convertToDatePickerProps(to)
    if (start && end) {
      setSelectedDate({
        startDate: start,
        endDate: end,
      })
    }
  }, [from, to])

 
  React.useEffect(() => {
    if (!from || !to || !driverId || !companyId) return

    const fetchTrips = async () => {
      const startDate = new Date(
        parseInt(from.split('-')[0]),
        parseInt(from.split('-')[1]) - 1,
        parseInt(from.split('-')[2]),
      )
      const endDate = new Date(
        parseInt(to.split('-')[0]),
        parseInt(to.split('-')[1]) - 1,
        parseInt(to.split('-')[2]),
      )

  
      const fetchStart = subMonths(startDate, 6)
      const fetchEnd = addMonths(endDate, 2)

      const result = await getBusinessTrips({
        companyId,
        drivers: [parseInt(driverId)],
        startTime: fetchStart,
        endTime: fetchEnd,
        filter: DateRangeFilter.Started,
        onlyPolish: polishOnlyFilter,
      })

      setTrips(result || [])
    }

    fetchTrips()
  }, [from, to, driverId, companyId, polishOnlyFilter, refreshKey])

  const filteredTrips = React.useMemo(() => {
    if (!from || !to || trips.length === 0) return trips

    const rangeStart = moment(from, 'YYYY-MM-DD').startOf('day')
    const rangeEnd = moment(to, 'YYYY-MM-DD').endOf('day')

    if (!rangeStart.isValid() || !rangeEnd.isValid()) return trips

    return trips.filter((trip) => {
      switch (dateRangeFilter) {
        case DateRangeFilter.Started: {
          const d = trip.startDate
          if (!d) return true
          const m = moment.isMoment(d) ? d : moment(d)
          return m.isSameOrAfter(rangeStart) && m.isSameOrBefore(rangeEnd)
        }
        case DateRangeFilter.Ended: {
          const d = trip.endDate
          if (!d) return true
          const m = moment.isMoment(d) ? d : moment(d)
          return m.isSameOrAfter(rangeStart) && m.isSameOrBefore(rangeEnd)
        }
        case DateRangeFilter.All:
        case DateRangeFilter.AllButSettlementMonth:
          return true
        case DateRangeFilter.Modified: {
          const d = trip.modificationDate
          if (!d) return true
          const m = moment.isMoment(d) ? d : moment(d)
          return m.isSameOrAfter(rangeStart) && m.isSameOrBefore(rangeEnd)
        }
        default:
          return true
      }
    })
  }, [trips, from, to, dateRangeFilter])

  return (
    <div className="tw-z-10 tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
      <Single
        trips={filteredTrips}
        companyId={companyId}
        selectedDriverId={driverId}
        onRefresh={handleRefresh}
        dateRangeFilter={dateRangeFilter}
        polishOnlyFilter={polishOnlyFilter}
        onDateRangeFilterChange={setDateRangeFilter}
        onPolishOnlyFilterChange={setPolishOnlyFilter}
        dateRange={selectedDate}
        match={match}
        history={history}
      />
    </div>
  )
}

export const DriverBusinessTripsPage = ({ match, history }: any) => {
  const { params } = match
  const { driverId, companyId, from, to } = params
  const { getDateRangeForView } = useDate()

  // Redirect to route with date parameters if they're missing
  React.useEffect(() => {
    if (
      driverId &&
      companyId &&
      (!from || !to || from.trim() === '' || to.trim() === '')
    ) {
      const { from: defaultFrom, to: defaultTo } = getDateRangeForView()
      const newPath = `/et/company/${companyId}/driverModule/driver/${driverId}/businessTrip/from/${defaultFrom}/to/${defaultTo}`
      history.replace(newPath)
    }
  }, [driverId, companyId, from, to, history])

  return (
      <TableProvider bulk={true}>
        <BusinessTripsContent
          match={match}
          history={history}
          companyId={companyId}
          driverId={driverId}
        />
      </TableProvider>
  )
}
